import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import {
  Box, Grid, Flex, Image, Heading, Text, Badge, Spinner
} from "@chakra-ui/react";
import InstrumentDetails from './InstrumentDetails'; // Import the detailed component

import { instrumentTexts } from "../i18n/instruments";
import { LanguageContext } from "../contexts/LanguageContext";
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Availability first, then anything unknown at the end
const STATUS_ORDER = { Available: 0, Reserved: 1, AgreementInProgress: 2, Rented: 3 };

const sortInstruments = (list) =>
  [...list].sort((a, b) => {
    const sa = STATUS_ORDER[a.status] ?? 99;
    const sb = STATUS_ORDER[b.status] ?? 99;
    if (sa !== sb) return sa - sb;
    return a.modelId.keys - b.modelId.keys;
  });

const SpecRow = ({ label, value }) => (
  <Flex justify="space-between" gap={3} py={1}>
    <Text fontSize="xs" color="gray.500">{label}</Text>
    <Text fontSize="xs" color="gray.700" fontWeight="medium" textAlign="right">{value}</Text>
  </Flex>
);

const AccordionList = ({ isLoggedIn }) => {
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(true); // For showing a loading state
  const [error, setError] = useState(null);
  const [selectedInstrument, setSelectedInstrument] = useState(null); // State for selected instrument

  const { language } = useContext(LanguageContext);
  const t = instrumentTexts[language] || instrumentTexts["et"];

  // Fetch data from the API
  useEffect(() => {
    const fetchInstruments = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/available-instruments/`);
        setInstruments(sortInstruments(response.data));
        setLoading(false);
      } catch (err) {
        setError(err);
        setLoading(false);
      }
    };

    fetchInstruments();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="60vh">
        <Spinner size="xl" color="gray.500" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" color="red.500" py={24} fontFamily="space" fontSize="sm">
        {t.error}: {error.message}
      </Box>
    );
  }

  if (selectedInstrument) {
    return (
      <Box p={{ base: 4, md: 8 }} bg="gray.50" minH="100vh">
        <InstrumentDetails
          instrument={selectedInstrument}
          onBack={() => setSelectedInstrument(null)}
          isLoggedIn={isLoggedIn}
        />
      </Box>
    );
  }

  return (
    <Box bg="gray.50" minH="100vh">
      {/* Dark editorial header band */}
      <Box bg="#09090b" pt={{ base: 9, md: 12 }} pb={{ base: 8, md: 10 }} px={6} textAlign="center">
        <Text
          fontFamily="space"
          fontSize="xs"
          letterSpacing="0.35em"
          textTransform="uppercase"
          color="gray.400"
          mb={4}
        >
          akordion.ee
        </Text>
        <Heading
          as="h1"
          fontFamily="heading"
          fontWeight="300"
          fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
          lineHeight="1.1"
          color="white"
        >
          {t.title}
        </Heading>
      </Box>

      {/* Catalog */}
      <Box maxW="7xl" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 8, md: 12 }}>
        <Grid
          templateColumns={{
            base: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
            xl: "repeat(4, 1fr)",
          }}
          gap={6}
        >
          {instruments.map((instrument) => (
            <Box
              key={instrument.instrumentId}
              bg="white"
              borderRadius="lg"
              borderWidth="1px"
              borderColor="gray.200"
              boxShadow="sm"
              overflow="hidden"
              transition="all 0.25s ease"
              _hover={{ boxShadow: "xl", transform: "translateY(-4px)" }}
              cursor="pointer"
              onClick={() => setSelectedInstrument(instrument)}
            >
              <Box bg="gray.100" h="220px" display="flex" alignItems="center" justifyContent="center">
                <Image
                  src={`${API_BASE_URL}${instrument.thumbnail_image}`}
                  alt={`${instrument.modelId.brand} ${instrument.modelId.model}`}
                  objectFit="contain"
                  w="100%"
                  h="100%"
                />
              </Box>

              <Box p={5}>
                <Text
                  fontFamily="space"
                  fontSize="11px"
                  letterSpacing="0.2em"
                  textTransform="uppercase"
                  color="gray.500"
                  mb={1}
                >
                  {instrument.modelId.brand}
                </Text>

                <Heading
                  as="h3"
                  fontFamily="heading"
                  fontWeight="600"
                  fontSize="2xl"
                  lineHeight="shorter"
                  color="gray.900"
                  mb={2}
                >
                  {instrument.modelId.model}
                </Heading>

                <Badge
                  colorPalette={instrument.status === "Available" ? "green" : "orange"}
                  variant="subtle"
                  fontFamily="space"
                  fontSize="10px"
                  letterSpacing="0.15em"
                  textTransform="uppercase"
                  px={2}
                  py={0.5}
                  borderRadius="sm"
                  mb={2}
                >
                  {t.status[instrument.status] || instrument.status}
                </Badge>

                {instrument.status !== "Available" && (
                  <Text fontSize="xs" color="gray.600" mb={1}>
                    {instrument.predicted_availability}
                  </Text>
                )}

                <Box borderTopWidth="1px" borderColor="gray.100" mt={3} pt={2}>
                  <SpecRow
                    label={t.color}
                    value={t.colors[instrument.color] || instrument.color}
                  />
                  <SpecRow label={t.keys} value={instrument.modelId.keys} />
                  <SpecRow label={t.basses} value={instrument.modelId.sb} />
                  <SpecRow
                    label={t.reeds}
                    value={`${instrument.modelId.reedsR}/${instrument.modelId.reedsL}`}
                  />
                  <SpecRow
                    label={t.dimensions}
                    value={`${instrument.modelId.height}cm × ${instrument.modelId.width}cm`}
                  />
                </Box>
              </Box>
            </Box>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default AccordionList;
